import {useTranslation} from '@aboutyou/i18n';
import React from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {mobileOnly} from '../../../constants/customMedia';
import {FONT_WEIGHTS} from '../../../constants/typography';
import {UnstyledButton} from '../Buttons/UnstyledButton';

interface QuantityChangeButtonProps {
  value: number;
  onChange: (quantity: number) => void;
  min?: number; // defaults to 0
  max?: number; // defaults to Infinity
  disabled?: boolean; // e.g. while a quantity change request is pending
}

export const QuantityChangeButton = ({
  value,
  onChange,
  min = 0,
  max = Infinity,
  disabled = false,
}: QuantityChangeButtonProps) => {
  const {t} = useTranslation();
  return (
    <Container data-testid="productQtyChangeContainer">
      <Button
        disabled={disabled || value === min}
        onClick={() => onChange(value - 1)}
        data-testid="productQtyDecreaseButton"
        aria-label={t('decrease')}
      >
        -
      </Button>
      <Value data-testid="selectedProductQty">{value}</Value>
      <Button
        disabled={disabled || value >= max}
        onClick={() => onChange(value + 1)}
        data-testid="productQtyIncreaseButton"
        aria-label={t('increase')}
      >
        +
      </Button>
    </Container>
  );
};

const Button = styled(UnstyledButton)`
  outline: 0;
  border: 0;
  cursor: pointer;
  background: none;
  padding: 7px 10px;
  color: ${COLORS.Grey1};
  font-weight: ${FONT_WEIGHTS.w400};
  margin-top: -2px;
  transition: color 0.3s ease;

  &:hover {
    color: ${COLORS.Black};
  }

  &:disabled {
    color: ${COLORS['Fill.Grey.Inactive']};
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px auto Highlight;
    outline: 2px auto -webkit-focus-ring-color;
  }
`;

const Value = styled.div`
  padding: 7px 10px;
  color: ${COLORS.Black};
  font-weight: ${FONT_WEIGHTS.w400};
  font-size: 0.9rem;
`;

const Container = styled.div`
  display: inline-flex;
  border: 1px solid ${COLORS.Grey3};
  border-radius: 4px;
  ${mobileOnly(css`
    justify-content: space-around;
    width: 100%;
  `)}
`;
